import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {NavLink, useNavigate} from 'react-router-dom';
import { Box, Button, FormControl, TextField, Typography, useTheme } from '@mui/material';
import { COLOR_GREEN, COLOR_LIGHT_GREY, COLOR_RED, COLOR_SUMMER_SKY, COLOR_WHITE } from 'popTheme';
import { sendPasswordResetSaga } from '../../redux/sagas/sendPasswordResetSaga';
import { clearPasswordResetEmailStatus } from '../../redux/reducers/authorization';
import ForgotPasswordBanner from './forgot-password-banner.png';

const PAUSE_TIME_AFTER_EMAIL_SENT_TO_REDIRECT_TO_SIGNIN = 5000;

export const ForgotPassword = () => {
  const theme = useTheme();
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { success, error } = useSelector((state) => state.authorization.passwordResetEmail);
  const [email, setEmail] = useState('');
  const isDisabled = email.length === 0;
  // NOTE: We capture this if we want to cancel the timeout
  const timeoutForSigninRedirect = useRef(null);

  const messages = defineMessages({
    forgotPasswordLabelBackLink: {
      id: 'forgot.password.label.back.link',
      defaultMessage: 'Back to Sign in',
      description: 'Link text for back to sign in',
    },
    forgotPasswordLabelHeader: {
      id: 'forgot.password.label.header',
      defaultMessage: 'Oops, did you forget your password?',
      description: 'Header text for forgot password page',
    },
    forgotPasswordLabelSecondary: {
      id: 'forgot.password.label.secondary',
      defaultMessage: 'Enter your email address below and we will send you a password reset link',
      description: 'Secondary text for forgot password page',
    },
    forgotPasswordLabelEmail: {
      id: 'forgot.password.label.email',
      defaultMessage: 'Email address:',
      description: 'Label for the email input',
    },
    forgotPasswordMessageSuccess: {
      id: 'forgot.password.label.success',
      defaultMessage: 'Reset password email sent',
      description: 'Submit success message',
    },
    forgotPasswordMessageError: {
      id: 'forgot.password.label.error',
      defaultMessage: 'Email failed to send',
      description: 'Submit error message',
    },
    forgotPasswordLabelButton: {
      id: 'forgot.password.label.button',
      defaultMessage: 'REQUEST RESET LINK',
      description: 'Label for the submit button',
    },
  });

  const onChange = (e) => setEmail(e.target.value);

  const handleSubmit = (e) => {
    dispatch(sendPasswordResetSaga(email));
    e.preventDefault();
  };

  const handleCancelSigninRedirect = () => {
      dispatch(clearPasswordResetEmailStatus());

      if (timeoutForSigninRedirect.current) {
          clearTimeout(timeoutForSigninRedirect.current);
      }
  }

  const handleSigninRedirect = useCallback(() => {
      // NOTE: We capture the `timeoutForSigninRedirect` timer reference in case we need to cancel the timer later
      timeoutForSigninRedirect.current = setTimeout(() => {
          dispatch(clearPasswordResetEmailStatus());
          navigate('/signin');
      }, PAUSE_TIME_AFTER_EMAIL_SENT_TO_REDIRECT_TO_SIGNIN);
  }, [dispatch, navigate]);

  useEffect(() => {
    if (success) {
        handleSigninRedirect();
    }
    return () => {
      if (timeoutForSigninRedirect.current) {
        clearTimeout(timeoutForSigninRedirect.current);
      }
    };
  }, [success, handleSigninRedirect]);

  return (
    <Box
      sx={{
        alignSelf: 'center',
        textAlign: 'center',
        margin: 'auto',
        width: '60%',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        p: '2rem',
        boxShadow: '0 0 0.3125rem #ccc',
        borderRadius: '0.75rem',
        background: COLOR_WHITE,
        [theme.breakpoints.down('sm')]: {
          width: '80%',
          p: '0.5rem',
        },
        // NOTE: This allows all the content to show on screens with a width of 375px or less (e.g. iPhone 5/SE 1st Gen, iPhone SE 2nd Gen)
        [theme.breakpoints.between(0, 376)]: {
          top: '0%',
          transform: 'translate(-50%, 0%)',
        },
      }}
    >
      <Typography
        sx={{
          p: '1.25rem',
          display: 'block',
          textAlign: 'left',
          fontSize: '1.25rem',
          fontWeight: '600',
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.875rem',
          },
        }}
      >
        <NavLink to="/signin" onClick={handleCancelSigninRedirect} style={{ textDecoration: 'none' }}>
          &lt; {intl.formatMessage(messages.forgotPasswordLabelBackLink)}
        </NavLink>
      </Typography>
      <img alt="Forgot password banner" src={ForgotPasswordBanner} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          maxWidth: '31.25rem',
          margin: '0 auto',
          [theme.breakpoints.down('sm')]: {
            p: '0.875rem',
          },
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2.4375rem' },
            mt: { xs: '1rem', sm: '2rem' },
          }}
        >
          {intl.formatMessage(messages.forgotPasswordLabelHeader)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' },
          }}
        >
          {intl.formatMessage(messages.forgotPasswordLabelSecondary)}
        </Typography>
        <FormControl sx={{ width: '100%' }}>
          <TextField
            id="email"
            type="email"
            autoComplete="email"
            variant="standard"
            data-testid="email-input"
            label={intl.formatMessage(messages.forgotPasswordLabelEmail)}
            onChange={onChange}
            sx={{
              '& label': {
                fontSize: '1rem',
              },
              '& label.Mui-focused': {
                fontSize: '1rem',
                color: 'black',
              },
              '& .MuiInputBase-input': {
                 fontSize: '1rem',
              },
              '& .MuiInput-underline:after': {
                bgcolor: 'yellow',
                borderBottom: '0.125rem solid black',
              },
            }}
          />
        </FormControl>
        {success && (
          <Typography variant="body1" sx={{ color: COLOR_GREEN, textAlign: 'left' }}>
            {intl.formatMessage(messages.forgotPasswordMessageSuccess)}
          </Typography>
        )}
        {error && (
          <Typography variant="body1" sx={{ color: COLOR_RED, textAlign: 'left' }}>
            {intl.formatMessage(messages.forgotPasswordMessageError)}
          </Typography>
        )}
        <Button
          disabled={isDisabled}
          onClick={handleSubmit}
          data-testid="request-reset-button"
          sx={{
            background: COLOR_SUMMER_SKY,
            border: 'none',
            borderRadius: '1.875rem',
            color: COLOR_WHITE,
            p: '0.625rem 2.5rem',
            textTransform: 'uppercase',
            alignSelf: 'center',
            textAlign: 'center',
            userSelect: 'none',
            fontWeight: '700',
            fontSize: '1rem',
            mt: '1rem',
            ':hover': {
              background: COLOR_SUMMER_SKY,
            },
            '&.Mui-disabled': {
              background: COLOR_LIGHT_GREY,
              color: COLOR_WHITE,
            },
          }}
        >
          {intl.formatMessage(messages.forgotPasswordLabelButton)}
        </Button>
      </Box>
    </Box>
  );
};
